import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import CountUp from "react-countup";
import {
  Shield, Landmark, Lightbulb, Timer,
  Search, Ruler, ShoppingCart, HardHat,
  ClipboardCheck, FileCheck, Monitor,
} from "lucide-react";

const TEAM_IMG =
  "https://images.pexels.com/photos/33438126/pexels-photo-33438126.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const pillars = [
  { icon: Shield, title: "Reliable", desc: "Trusted by international MNCs. Critical component suppliers to Mercedes, Mahindra, Maruti, and Toyota rely on our solar infrastructure." },
  { icon: Landmark, title: "Bankable", desc: "Backed by global climate investment funds. $50M+ raised since 2018 with a committed pipeline of 500 MW by 2030." },
  { icon: Lightbulb, title: "Innovative", desc: "Tailored financial models \u2014 RESCO, CapEx, hybrid \u2014 ensuring flexibility and perfect alignment with your business objectives." },
  { icon: Timer, title: "Enduring", desc: "Building robust, long-lasting solar plants with 25-year PPAs. Serving clients like Nestle and F-Tech Japan." },
];

const processSteps = [
  { icon: Search, title: "Site Analysis" },
  { icon: Ruler, title: "System Design" },
  { icon: ShoppingCart, title: "Procurement" },
  { icon: HardHat, title: "Installation" },
  { icon: ClipboardCheck, title: "Inspection" },
  { icon: FileCheck, title: "Permitting" },
  { icon: Monitor, title: "Monitoring" },
];

const team = [
  { name: "Tejasvi Verma", role: "Co-Founder & CEO", email: "tejasvi.verma@energyx.in" },
  { name: "Neel Sahni", role: "Co-Founder & COO", email: "" },
  { name: "Saurabh Sharma", role: "Co-Founder & CTO", email: "" },
];

export default function AboutPage() {
  const statsRef = useRef(null);
  const statsInView = useInView(statsRef, { once: true });

  return (
    <div>
      {/* ── Hero ─────────────────────────────────────────── */}
      <section className="relative pt-32 pb-24 bg-[#0A1628] overflow-hidden">
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `url(${TEAM_IMG})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-[#0A1628]/80" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#00D68F] mb-4">
              About EnergyX
            </p>
            <h1 className="text-5xl md:text-6xl font-black tracking-tighter text-white">
              Built for India's Clean
              <br />
              <span className="text-[#00D68F]">Energy Transition</span>
            </h1>
          </motion.div>
        </div>
      </section>

      {/* ── Story ────────────────────────────────────────── */}
      <motion.section
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.7 }}
        className="py-24 md:py-32 bg-white"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-7">
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-[#0A1628] mb-8">
              Your Comprehensive Energy Partner
            </h2>
            <div className="space-y-4 text-[#0A1628]/70 text-base leading-relaxed">
              <p>
                EnergyX is headquartered in Gurugram (Delhi NCR) and is a
                comprehensive Energy Partner to the C&I segment. We provide
                end-to-end solar solutions {"\u2014"} from securing investment and
                installing onsite systems to long-term operations and
                maintenance.
              </p>
              <p>
                Backed by marquee investors from India, Japan, and Europe, we
                are committed to investing USD 100 Million in the Indian solar
                industry under Corporate PPAs, with a committed pipeline of 500
                MW by 2030.
              </p>
            </div>
          </div>
          <div ref={statsRef} className="md:col-span-5 grid grid-cols-2 gap-6">
            {[
              { val: 100, prefix: "$", suffix: "M+", label: "Committed Investment" },
              { val: 500, suffix: " MW", label: "Pipeline by 2030" },
              { val: 30, suffix: " MW+", label: "Installed Capacity" },
              { val: 25, suffix: "+ Years", label: "PPA Duration" },
            ].map((s, i) => (
              <div key={i} className="bg-[#F5F7FA] p-6 text-center">
                <div className="text-3xl font-black text-[#00D68F]">
                  {s.prefix}
                  {statsInView ? (
                    <CountUp end={s.val} duration={2} />
                  ) : (
                    0
                  )}
                  {s.suffix}
                </div>
                <div className="text-[#0A1628]/60 text-sm mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* ── Four Pillars ─────────────────────────────────── */}
      <motion.section
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.7 }}
        className="py-24 md:py-32 bg-[#0A1628]"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#00D68F] mb-4">
            Our Advantage
          </p>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-white mb-16">
            The EnergyX Advantage
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {pillars.map((p, i) => {
              const Icon = p.icon;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.15 }}
                  className="bg-white/5 backdrop-blur-xl border border-white/10 p-8 group hover:bg-white/10 transition-all"
                  data-testid={`pillar-${i}`}
                >
                  <Icon size={32} className="text-[#00D68F] mb-4" />
                  <h3 className="text-white text-xl font-bold mb-3">
                    {p.title}
                  </h3>
                  <p className="text-white/60 text-sm leading-relaxed">
                    {p.desc}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </motion.section>

      {/* ── EaaS Process ─────────────────────────────────── */}
      <motion.section
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.7 }}
        className="py-24 md:py-32 bg-white"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#00D68F] mb-4">
            Process
          </p>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-[#0A1628] mb-16">
            How EaaS Works
          </h2>
          <div className="flex flex-wrap justify-center gap-4 md:gap-2">
            {processSteps.map((s, i) => {
              const Icon = s.icon;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="flex items-center gap-3"
                >
                  <div className="bg-[#0A1628] w-14 h-14 flex items-center justify-center shrink-0">
                    <Icon size={24} className="text-[#00D68F]" />
                  </div>
                  <span className="text-sm font-bold text-[#0A1628]">
                    {s.title}
                  </span>
                  {i < processSteps.length - 1 && (
                    <div className="w-8 h-[2px] bg-[#00D68F]/30 hidden md:block" />
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>
      </motion.section>

      {/* ── Team ─────────────────────────────────────────── */}
      <motion.section
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.7 }}
        className="py-24 md:py-32 bg-[#F5F7FA]"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#00D68F] mb-4">
            Leadership
          </p>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-[#0A1628] mb-16">
            Our Team
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {team.map((t, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15 }}
                className="bg-white border border-gray-200 p-8 text-center group hover:shadow-xl transition-all"
                data-testid={`team-member-${i}`}
              >
                <div className="w-24 h-24 mx-auto bg-[#0A1628] flex items-center justify-center text-3xl font-black text-[#00D68F] mb-4">
                  {t.name
                    .split(" ")
                    .map((n) => n[0])
                    .join("")}
                </div>
                <h3 className="text-xl font-bold text-[#0A1628]">{t.name}</h3>
                <p className="text-[#0A1628]/60 text-sm mt-1">{t.role}</p>
                {t.email && (
                  <p className="text-[#00D68F] text-sm mt-2">{t.email}</p>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>
    </div>
  );
}
